import { format, isValid, parse } from "date-fns";

/**
 * Common date formats that AI might extract from documents
 * Ordered by specificity to avoid false matches
 */
const COMMON_DATE_FORMATS = [
  // Full month name with comma
  "MMMM d, yyyy", // "June 3, 2022"
  "MMM d, yyyy", // "Jun 3, 2022"

  // Full month name without comma
  "MMMM d yyyy", // "June 3 2022"
  "MMM d yyyy", // "Jun 3 2022"

  // Year first patterns
  "yyyy MMMM d", // "2022 June 3"
  "yyyy MMM d", // "2022 Jun 3"
  "yyyy, MMMM d", // "2022, June 3"
  "yyyy, MMM d", // "2022, Jun 3"

  // Day first patterns (European style)
  "d MMMM yyyy", // "3 June 2022"
  "d MMM yyyy", // "3 Jun 2022"
  "d MMMM, yyyy", // "3 June, 2022"
  "d MMM, yyyy", // "3 Jun, 2022"

  // Mixed separators with month names
  "yyyy/MMMM/d", // "2022/June/3"
  "yyyy/MMM/d", // "2022/Jun/3"
  "d/MMMM/yyyy", // "3/June/2022"
  "d/MMM/yyyy", // "3/Jun/2022"
  "yyyy-MMMM-d", // "2022-June-3"
  "yyyy-MMM-d", // "2022-Jun-3"
  "d-MMMM-yyyy", // "3-June-2022"
  "d-MMM-yyyy", // "3-Jun-2022"

  // Periods as separators
  "d.MMMM.yyyy", // "3.June.2022"
  "d.MMM.yyyy", // "3.Jun.2022"
  "yyyy.MMMM.d", // "2022.June.3"
  "yyyy.MMM.d", // "2022.Jun.3"

  // Numeric formats (most ambiguous, so last)
  "MM/dd/yyyy", // "06/03/2022"
  "M/d/yyyy", // "6/3/2022"
  "dd/MM/yyyy", // "03/06/2022" (European format)
  "d/M/yyyy", // "3/6/2022"
  "MM-dd-yyyy", // "06-03-2022"
  "M-d-yyyy", // "6-3-2022"
  "dd-MM-yyyy", // "03-06-2022"
  "d-M-yyyy", // "3-6-2022"
  "yyyy-MM-dd", // "2022-06-03" (ISO format)
  "yyyy/MM/dd", // "2022/06/03"
  "yyyy/M/d", // "2022/6/3"
  "dd.MM.yyyy", // "03.06.2022"
  "d.M.yyyy", // "3.6.2022"
];

/**
 * Attempts to parse a natural language date string into ISO format (yyyy-MM-dd)
 * @param dateString - The date string to parse (e.g., "June 3, 2022")
 * @returns ISO formatted date string (e.g., "2022-06-03") or null if parsing fails
 */
export function parseNaturalDateToISO(dateString: string): string | null {
  if (!dateString || typeof dateString !== "string") {
    return null;
  }

  const trimmedDate = dateString.trim();

  // If already in ISO format, return as-is
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmedDate)) {
    return trimmedDate;
  }

  // Try parsing with each known format
  for (const formatString of COMMON_DATE_FORMATS) {
    try {
      const parsedDate = parse(trimmedDate, formatString, new Date());
      if (isValid(parsedDate)) {
        return format(parsedDate, "yyyy-MM-dd");
      }
    } catch (error) {
      // Continue to next format
    }
  }

  // If all parsing attempts fail, try native Date parsing as last resort
  try {
    const nativeDate = new Date(trimmedDate);
    if (isValid(nativeDate) && !isNaN(nativeDate.getTime())) {
      return format(nativeDate, "yyyy-MM-dd");
    }
  } catch (error) {
    // Fall through to return null
  }

  return null;
}

/**
 * Checks if two date strings represent the same date, handling different formats
 * @param date1 - First date string (any format)
 * @param date2 - Second date string (any format)
 * @returns true if the dates are the same, false otherwise
 */
export function areDatesEqual(date1: string, date2: string): boolean {
  const iso1 = parseNaturalDateToISO(date1);
  const iso2 = parseNaturalDateToISO(date2);

  if (!iso1 || !iso2) {
    return false;
  }

  return iso1 === iso2;
}

/**
 * Formats a date string for display in a user-friendly format
 * @param dateString - Date string in any format
 * @param displayFormat - Format to display (default: "MMMM d, yyyy")
 * @returns Formatted date string or original string if parsing fails
 */
export function formatDateForDisplay(
  dateString: string,
  displayFormat: string = "MMMM d, yyyy"
): string {
  const isoDate = parseNaturalDateToISO(dateString);

  if (!isoDate) {
    return dateString; // Return original if parsing fails
  }

  try {
    const date = parse(isoDate, "yyyy-MM-dd", new Date());
    return format(date, displayFormat);
  } catch (error) {
    return dateString;
  }
}
